'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Pause, Play, Volume2, VolumeX, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';

interface VideoEventHandlers {
  onPlay?: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
  onPause?: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
  onEnded?: () => void;
  onLoadedMetadata?: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
  onTimeUpdate?: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
}

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoSrc: string;
  onTimeUpdate?: (currentTime: number) => void;
  videoEventHandlers?: VideoEventHandlers;
}

function formatTime(seconds: number): string {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function VideoModal({
  isOpen,
  onClose,
  videoSrc,
  onTimeUpdate: onTimeUpdateProp,
  videoEventHandlers,
}: VideoModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !isDragging) setShowControls(false);
    }, 3000);
  }, [isPlaying, isDragging]);

  // Handle modal open/close state
  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }

    if (isOpen && videoRef.current) {
      videoRef.current.volume = volume;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, videoRef.current]);

  // Auto-hide controls timeout
  useEffect(() => {
    if (isOpen) resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isOpen, resetControlsTimeout]);

  // Global mouse up listener for drag release
  useEffect(() => {
    if (!isDragging) return;
    const handleGlobalMouseUp = () => setIsDragging(false);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging]);

  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    resetControlsTimeout();
  }, [isPlaying, resetControlsTimeout]);

  const handleMuteToggle = useCallback(() => {
    if (!videoRef.current) return;
    if (isMuted) {
      videoRef.current.muted = false;
      videoRef.current.volume = previousVolume;
      setVolume(previousVolume);
      setIsMuted(false);
    } else {
      setPreviousVolume(volume);
      videoRef.current.muted = true;
      setIsMuted(true);
    }
    resetControlsTimeout();
  }, [isMuted, volume, previousVolume, resetControlsTimeout]);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!videoRef.current) return;
      const newVolume = parseFloat(e.target.value);
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      if (newVolume === 0) {
        videoRef.current.muted = true;
        setIsMuted(true);
      } else {
        videoRef.current.muted = false;
        setIsMuted(false);
        setPreviousVolume(newVolume);
      }
      resetControlsTimeout();
    },
    [resetControlsTimeout],
  );

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!videoRef.current || !progressRef.current) return;
      const video = videoRef.current;
      if (isNaN(video.duration) || !isFinite(video.duration)) return;

      const rect = progressRef.current.getBoundingClientRect();
      const clickPosition = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      video.currentTime = clickPosition * video.duration;
      setCurrentTime(video.currentTime);
      resetControlsTimeout();
    },
    [resetControlsTimeout],
  );

  const handleTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      if (videoRef.current && !isDragging) {
        setCurrentTime(videoRef.current.currentTime);
        onTimeUpdateProp?.(videoRef.current.currentTime);
        videoEventHandlers?.onTimeUpdate?.(e);
      }
    },
    [isDragging, onTimeUpdateProp, videoEventHandlers],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-5xl translate-x-[-50%] translate-y-[-50%]"
        >
          <DialogContent
            className="border-none bg-transparent p-0 shadow-none max-w-none w-full [&>button]:hidden"
            onPointerDownOutside={(e) => e.preventDefault()}
            aria-describedby={undefined}
          >
            <DialogTitle className="sr-only">Product Demo Video</DialogTitle>
            <DialogDescription className="sr-only">
              Watch the Bite Club product demo video showing how the platform helps reduce food
              waste
            </DialogDescription>
            <div
              className="relative w-full rounded-xl overflow-hidden bg-black shadow-2xl"
              onMouseMove={resetControlsTimeout}
            >
              {/* Close Button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: showControls ? 1 : 0 }}
                transition={{ duration: 0.2 }}
                onClick={onClose}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm 
                         border border-white/10 flex items-center justify-center hover:bg-black/70 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-white" />
              </motion.button>

              {/* Video */}
              <video
                ref={videoRef}
                className="w-full aspect-video cursor-pointer"
                onClick={handlePlayPause}
                onPlay={(e) => {
                  setIsPlaying(true);
                  videoEventHandlers?.onPlay?.(e);
                }}
                onPause={(e) => {
                  setIsPlaying(false);
                  videoEventHandlers?.onPause?.(e);
                }}
                onEnded={() => {
                  setIsPlaying(false);
                  setShowControls(true);
                  videoEventHandlers?.onEnded?.();
                }}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={(e) => {
                  if (videoRef.current) setDuration(videoRef.current.duration);
                  videoEventHandlers?.onLoadedMetadata?.(e);
                }}
                onDurationChange={() => videoRef.current && setDuration(videoRef.current.duration)}
                playsInline
                preload="auto"
                autoPlay
              >
                <source src={videoSrc} type="video/mp4" />
              </video>

              {/* Custom Controls */}
              <AnimatePresence>
                {showControls && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.2 }}
                    className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent"
                  >
                    {/* Progress Bar */}
                    <div
                      ref={progressRef}
                      onMouseDown={(e) => {
                        setIsDragging(true);
                        handleProgressClick(e);
                      }}
                      onMouseMove={(e) => isDragging && handleProgressClick(e)}
                      onMouseUp={() => setIsDragging(false)}
                      className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-4 group hover:h-2 transition-all"
                    >
                      <div
                        className="h-full bg-primary rounded-full relative"
                        style={{ width: `${progress}%` }}
                      >
                        <div
                          className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full 
                                        scale-0 group-hover:scale-100 transition-transform shadow-lg"
                        />
                      </div>
                    </div>

                    {/* Control Buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handlePlayPause}
                          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 
                                   flex items-center justify-center hover:bg-white/20 transition-colors"
                          aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                          {isPlaying ? (
                            <Pause className="w-5 h-5 text-white" />
                          ) : (
                            <Play className="w-5 h-5 text-white ml-0.5" />
                          )}
                        </button>
                        <div className="text-white text-sm font-mono tabular-nums">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </div>
                      </div>

                      {/* Volume Controls */}
                      <div className="group/volume flex items-center gap-2">
                        <button
                          onClick={handleMuteToggle}
                          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 
                                   flex items-center justify-center hover:bg-white/20 transition-colors"
                          aria-label={isMuted ? 'Unmute' : 'Mute'}
                        >
                          {isMuted || volume === 0 ? (
                            <VolumeX className="w-5 h-5 text-white" />
                          ) : (
                            <Volume2 className="w-5 h-5 text-white" />
                          )}
                        </button>
                        <div className="overflow-hidden w-0 group-hover/volume:w-24 transition-all duration-300">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer
                                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 
                                     [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full 
                                     [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer
                                     [&::-webkit-slider-thumb]:shadow-lg
                                     [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 
                                     [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white
                                     [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer"
                            aria-label="Volume"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Center Play Button (when paused) */}
              <AnimatePresence>
                {!isPlaying && showControls && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    onClick={handlePlayPause}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                             w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 
                             flex items-center justify-center hover:bg-white/20 hover:scale-110 
                             transition-all duration-300 shadow-2xl"
                    aria-label="Play"
                  >
                    <Play className="w-8 h-8 text-white ml-1" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </DialogContent>
        </motion.div>
      </DialogPortal>
    </Dialog>
  );
}
